import React, { useRef, useState } from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';
import { t } from '@konrad/reweb-aem';

import { SelectInput, TextInput } from '../_shared';
import { Button, Icon, Text } from '../index';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';
import { CURRENCY_MASK_OPTIONS, MORTGAGE_CALCULATOR } from '../../lib/constants';
import { getHelpTextId, removeSymbols } from '../../lib/helpers/form';

import styles from './MortgageCalculator.module.scss';
import { useReactToPrint } from 'react-to-print';

const MortgageCalculator = (props) => {
    const {
        typeOfLoanTitle,
        typeOfLoanSubtitle,
        typeOfLoanFirstTooltip,
        typeOfLoanSecondTooltip,
        loanAmountTitle,
        loanAmountSubtitle,
        homeValueTitle,
        homeValueSubtitle,
        homeValueTooltip,
        frequencyTitle,
        frequencySubtitle,
        loanTermTitle,
        loanTermSubtitle,
        amortizationPeriodTitle,
        amortizationPeriodSubtitle,
        borrowingTitle,
        borrowingOption1,
        borrowingOption1Description,
        borrowingOption2,
        borrowingOption2Description,
        borrowingOption3,
        borrowingOption3Description,
        borrowingOption4,
        borrowingOption4Description,
        resultTitle,
        resultSubtitle,
        ctaLinkAltText,
        ctaLinkText,
        ctaLinkType,
        ctaLinkUrl,
        downloadText,
    } = props;

    const rootRef = useRef();
    const handlePrint = useReactToPrint({
        content: () => rootRef.current,
    });

    const locale = document.documentElement.lang;
    const currencyMaskEn = createNumberMask(CURRENCY_MASK_OPTIONS.EN);
    const currencyMaskFr = createNumberMask(CURRENCY_MASK_OPTIONS.FR);
    const typeOfLoanId = 'type-of-loan';
    const typeOfLoanHelpText = getHelpTextId(typeOfLoanId);
    const loanAmountId = 'loan-amount';
    const loanAmountHelpText = getHelpTextId(loanAmountId);
    const homeValueId = 'home-value';
    const homeValueHelpText = getHelpTextId(homeValueId);
    const loanTermId = 'loan-term';
    const loanTermHelpText = getHelpTextId(loanTermId);
    const frequencyId = 'frequency';
    const frequencyHelpText = getHelpTextId(frequencyId);
    const amortizationPeriodId = 'amortizaiton-period';
    const amortizationPeriodHelpText = getHelpTextId(amortizationPeriodId);
    const borrowingId = 'borrowing';
    const borrowingDescriptionId = 'borrowing-description';

    const resultId = 'result';
    const resultHelpText = getHelpTextId(resultId);

    const typeOfLoanOptions = [
        {
            name: t('fs.mortgage.calculator-mortgage-refinancing'),
            value: MORTGAGE_CALCULATOR.MORTGAGE_REFINANCING.name,
        },
        {
            name: t('fs.mortgage.calculator-second-mortgage'),
            value: MORTGAGE_CALCULATOR.SECOND_MORTGAGE.name,
        },
    ];

    const borrowingOptions = [
        {
            name: borrowingOption4,
            value: '3',
            description: borrowingOption4Description,
        },
        {
            name: borrowingOption3,
            value: '2',
            description: borrowingOption3Description,
        },
        {
            name: borrowingOption2,
            value: '1',
            description: borrowingOption2Description,
        },
        {
            name: borrowingOption1,
            value: '0',
            description: borrowingOption1Description,
        },
    ];

    const frequencyOptions = [
        {
            name: t('fs.loan-payment-calculator.frequency-weekly-option'),
            value: 52,
        },
        {
            name: t('fs.loan-payment-calculator.frequency-bi-weekly-option'),
            value: 26,
        },
        {
            name: t('fs.loan-payment-calculator.frequency-semi-monthly-option'),
            value: 24,
        },
        {
            name: t('fs.loan-payment-calculator.frequency-monthly-option'),
            value: 12,
        },
    ];

    const amortizationYears = [
        {
            name: t('fs.mortgage.calculator-years', [5]),
            value: 5,
        },
        {
            name: t('fs.mortgage.calculator-years', [10]),
            value: 10,
        },
        {
            name: t('fs.mortgage.calculator-years', [15]),
            value: 15,
        },
        {
            name: t('fs.mortgage.calculator-years', [20]),
            value: 20,
        },
        {
            name: t('fs.mortgage.calculator-years', [25]),
            value: 25,
        },
        {
            name: t('fs.mortgage.calculator-years', [30]),
            value: 30,
        },
    ];

    const loanTermOptions = [
        {
            name: t('fs.loan-payment-calculator.loan-term-months-option', [12]),
            value: 12,
        },
        {
            name: t('fs.loan-payment-calculator.loan-term-months-option', [24]),
            value: 24,
        },
        {
            name: t('fs.loan-payment-calculator.loan-term-months-option', [26]),
            value: 26,
        },
        {
            name: t('fs.loan-payment-calculator.loan-term-months-option', [48]),
            value: 48,
        },
        {
            name: t('fs.loan-payment-calculator.loan-term-months-option', [60]),
            value: 60,
        },
    ];

    const fsreProduct = [
        {
            name: typeOfLoanOptions[0].name,
            value: typeOfLoanOptions[0].value,
            fsre: {
                l75: [0.1725, 0.18, 0.195, 0.1975],
                b75: [0.175, 0.1825, 0.1975, 0.2],
                o80: 0.0,
            },
        },
        {
            name: typeOfLoanOptions[1].name,
            value: typeOfLoanOptions[1].value,
            fsre: {
                l75: [0.1865, 0.1925, 0.1975, 0.205],
                b75: [0.189, 0.195, 0.2, 0.21],
                o80: 0.0,
            },
        },
    ];

    const [input, setInput] = useState({
        [typeOfLoanId]: typeOfLoanOptions[1].value,
        [loanAmountId]: 100000,
        [homeValueId]: 700000,
        [frequencyId]: frequencyOptions[1].value,
        [loanTermId]: loanTermOptions[1].value,
        [amortizationPeriodId]: amortizationYears[3].value,
        [borrowingId]: borrowingOptions[3].value,
    });

    const [amortizationOptions, setAmortizationOptions] = useState(amortizationYears);

    //Validations
    const emptyInputs = input[loanAmountId] === 0 || input[homeValueId] === 0;
    const invalidLvt = input[loanAmountId] / input[homeValueId] > 0.8;
    const invalidLoanMax =
        input[typeOfLoanId] === MORTGAGE_CALCULATOR.SECOND_MORTGAGE.name
            ? input[loanAmountId] > MORTGAGE_CALCULATOR.SECOND_MORTGAGE.max
            : input[loanAmountId] > MORTGAGE_CALCULATOR.MORTGAGE_REFINANCING.max;
    const invalidLoanMin =
        input[loanAmountId] < MORTGAGE_CALCULATOR.SECOND_MORTGAGE.min ||
        input[loanAmountId] < MORTGAGE_CALCULATOR.MORTGAGE_REFINANCING.min;

    const borrowingDescription = borrowingOptions.find(
        (option) => option.value === input[borrowingId]
    ).description;

    const handleInputChange = (event) => {
        const target = event.target;
        const name = target.name;
        let value = target.value;

        if (name === homeValueId || name === loanAmountId || name === frequencyId) {
            value = Number(removeSymbols(value));
        }

        if (name === typeOfLoanId) {
            if (value === typeOfLoanOptions[1].value) {
                setAmortizationOptions(amortizationYears.slice(0, 4));
            } else {
                setAmortizationOptions(amortizationYears);
            }
        }

        setInput({
            ...input,
            [name]: value,
        });
    };

    const getResults = () => {
        if (!invalidLoanMax && !invalidLoanMin) {
            const lstPercentage = (input[loanAmountId] / input[homeValueId]) * 100;
            let fsreRate;

            const fsreOption = fsreProduct.find((option) => option.value === input[typeOfLoanId]);

            if (lstPercentage < 75) {
                fsreRate = fsreOption.fsre.l75[input[borrowingId]];
            } else if (lstPercentage >= 75 && lstPercentage <= 80) {
                fsreRate = fsreOption.fsre.b75[input[borrowingId]];
            } else if (lstPercentage > 80) {
                fsreRate = fsreOption.fsre.o80[input[borrowingId]];
            }

            const numberOfPeriods = input[amortizationPeriodId] * input[frequencyId];
            const presentValue = input[loanAmountId];
            const rate = Math.pow(1 + fsreRate / 2, 2 / input[frequencyId]) - 1;
            const pvif = Math.pow(1 + rate, numberOfPeriods);

            const pmt = (rate / (pvif - 1)) * (presentValue * pvif);

            return Math.floor(pmt).toLocaleString(locale, {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
            });
        }
        return 0;
    };

    const getRadioElements = (groupId, options) => {
        return options.map((option) => {
            const inputId = `${groupId}-${option.value}`;

            return (
                <div className={styles['radio-option']} key={option.value}>
                    <input
                        aria-describedby={borrowingDescriptionId}
                        defaultChecked={input[groupId] === option.value}
                        className={styles['radio-input']}
                        id={inputId}
                        name={groupId}
                        onChange={handleInputChange}
                        type="radio"
                        value={option.value}
                    />
                    <label className={styles['radio-button']} htmlFor={inputId}>
                        {option.name}
                    </label>
                </div>
            );
        });
    };

    const getLoanErrorMessage = () => {
        if (invalidLoanMax) {
            if (input[typeOfLoanId] === MORTGAGE_CALCULATOR.SECOND_MORTGAGE.name) {
                return t('fs.mortgage.calculator-max-loan-amount-second-mortgage');
            } else {
                return t('fs.mortgage.calculator-max-loan-amount-mortgage-refinancing');
            }
        } else if (invalidLoanMin) {
            return t('fs.mortgage.calculator-loan-error-min');
        }
    };

    const getTooltip = () => {
        if (input[typeOfLoanId] === MORTGAGE_CALCULATOR.SECOND_MORTGAGE.name) {
            return typeOfLoanSecondTooltip;
        } else {
            return typeOfLoanFirstTooltip;
        }
    };

    return (
        <div className={styles.root} ref={rootRef}>
            <form
                className={cx('light-theme', styles.form)}
                onSubmit={(event) => event.preventDefault()}
            >
                <SelectInput
                    containerClassName={styles['input-container--regular']}
                    fieldClassName={styles['select-field']}
                    handleInputChange={handleInputChange}
                    helpTextId={typeOfLoanHelpText}
                    id={typeOfLoanId}
                    isInvalid={false}
                    options={typeOfLoanOptions}
                    subtitle={typeOfLoanSubtitle}
                    subtitleClassName={styles.subtitle}
                    title={typeOfLoanTitle}
                    value={input[typeOfLoanId]}
                    tooltip={getTooltip()}
                />
                <TextInput
                    containerClassName={styles['input-container--regular']}
                    fieldClassName={styles['text-field']}
                    handleInputChange={handleInputChange}
                    helpTextId={loanAmountHelpText}
                    id={loanAmountId}
                    mask={locale === 'fr' ? currencyMaskFr : currencyMaskEn}
                    subtitle={loanAmountSubtitle}
                    subtitleClassName={styles.subtitle}
                    title={loanAmountTitle}
                    value={input[loanAmountId]}
                    errorMessage={getLoanErrorMessage()}
                    isInvalid={invalidLoanMax || invalidLoanMin}
                />
                <TextInput
                    containerClassName={styles['input-container--regular']}
                    fieldClassName={styles['text-field']}
                    handleInputChange={handleInputChange}
                    helpTextId={homeValueHelpText}
                    id={homeValueId}
                    mask={locale === 'fr' ? currencyMaskFr : currencyMaskEn}
                    subtitle={homeValueSubtitle}
                    subtitleClassName={styles.subtitle}
                    title={homeValueTitle}
                    value={input[homeValueId]}
                    tooltip={homeValueTooltip}
                />
                <SelectInput
                    containerClassName={styles['input-container--regular']}
                    fieldClassName={styles['select-field']}
                    handleInputChange={handleInputChange}
                    helpTextId={frequencyHelpText}
                    id={frequencyId}
                    isInvalid={false}
                    options={frequencyOptions}
                    subtitle={frequencySubtitle}
                    subtitleClassName={styles.subtitle}
                    title={frequencyTitle}
                    value={input[frequencyId]}
                />
                <SelectInput
                    containerClassName={styles['input-container--regular']}
                    fieldClassName={styles['select-field']}
                    handleInputChange={handleInputChange}
                    helpTextId={loanTermHelpText}
                    id={loanTermId}
                    isInvalid={false}
                    options={loanTermOptions}
                    subtitle={loanTermSubtitle}
                    subtitleClassName={styles.subtitle}
                    title={loanTermTitle}
                    value={input[loanTermId]}
                />
                <SelectInput
                    containerClassName={styles['input-container--regular']}
                    fieldClassName={styles['select-field']}
                    handleInputChange={handleInputChange}
                    helpTextId={amortizationPeriodHelpText}
                    id={amortizationPeriodId}
                    isInvalid={false}
                    options={amortizationOptions}
                    subtitle={amortizationPeriodSubtitle}
                    subtitleClassName={styles.subtitle}
                    title={amortizationPeriodTitle}
                    value={input[amortizationPeriodId]}
                />
                <div className={styles['input-container--borrowing']}>
                    <div className={styles.title}>{borrowingTitle}</div>
                    <div className={styles['radio-options-container']} role="group">
                        {getRadioElements(borrowingId, borrowingOptions)}
                    </div>
                    <Text
                        className={styles.description}
                        id={borrowingDescriptionId}
                        content={borrowingDescription}
                    />
                </div>
                {emptyInputs && (
                    <div className={styles['error-message-container']}>
                        <Icon
                            className={cx(styles['error-message-icon'])}
                            size="x-small"
                            url="/etc.clientlibs/fs/clientlibs/clientlib-base/resources/icons/attention.svg"
                        />
                        <div className={styles['error-message-text']}>
                            {t('fs.mortgage.calculator-form-error')}
                        </div>
                    </div>
                )}
            </form>
            <div className={cx('dark-theme', styles['results-container'])}>
                <div className={styles['results-title']} id={resultId}>
                    {invalidLvt ? (
                        <Icon
                            size="x-large"
                            url="/etc.clientlibs/fs/clientlibs/clientlib-base/resources/icons/attention.svg"
                        />
                    ) : (
                        resultTitle
                    )}
                </div>
                <div
                    aria-describedby={resultHelpText}
                    aria-labelledby={resultId}
                    className={
                        invalidLvt ? styles['results-payment--error'] : styles['results-payment']
                    }
                >
                    {invalidLvt
                        ? t('fs.mortgage.calculator-lvt-error')
                        : t('fs.mortgage.calculator-result', [getResults()])}
                    <span className={styles['results-payment-asterisk']}>*</span>
                </div>
                <div id={resultHelpText} className={styles['results-subtitle']}>
                    <Text
                        className={styles['transcription']}
                        content={resultSubtitle}
                        size="body"
                    />
                </div>
                {ctaLinkText && ctaLinkUrl && (
                    <Button
                        altText={ctaLinkAltText || undefined}
                        className={styles.cta}
                        linkText={ctaLinkText}
                        linkType={ctaLinkType}
                        linkUrl={ctaLinkUrl}
                    />
                )}
                {!!window.print && downloadText && (
                    <Button
                        className={styles['download-cta']}
                        linkText={downloadText}
                        onClick={handlePrint}
                    />
                )}
            </div>
        </div>
    );
};

MortgageCalculator.displayName = 'MortgageCalculator';

MortgageCalculator.propTypes = {
    typeOfLoanTitle: PropTypes.string.isRequired,
    typeOfLoanSubtitle: PropTypes.string,
    loanAmountTitle: PropTypes.string.isRequired,
    loanAmountSubtitle: PropTypes.string,
    homeValueTitle: PropTypes.string.isRequired,
    homeValueSubtitle: PropTypes.string,
    frequencyTitle: PropTypes.string.isRequired,
    frequencySubtitle: PropTypes.string,
    loanTermTitle: PropTypes.string.isRequired,
    loanTermSubtitle: PropTypes.string,
    amortizationPeriodTitle: PropTypes.string.isRequired,
    amortizationPeriodSubtitle: PropTypes.string,
    borrowingTitle: PropTypes.string.isRequired,
    borrowingOption1: PropTypes.string.isRequired,
    borrowingOption1Description: PropTypes.string.isRequired,
    borrowingOption2: PropTypes.string.isRequired,
    borrowingOption2Description: PropTypes.string.isRequired,
    borrowingOption3: PropTypes.string.isRequired,
    borrowingOption3Description: PropTypes.string.isRequired,
    borrowingOption4: PropTypes.string.isRequired,
    borrowingOption4Description: PropTypes.string.isRequired,
    resultTitle: PropTypes.string.isRequired,
    resultSubtitle: PropTypes.string,
    buttonText: PropTypes.string.isRequired,
    buttonAltText: PropTypes.string.isRequired,
};

export default MortgageCalculator;
